/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from "@angular/router";
import { ObjectDefinition } from "../models/object-definition";
import { ObjectService } from "../services/object.service";
import { ObjectCategory } from "../models/object-category";
import { PAGING_ITEMS_PER_PAGE } from "../app-constants";
import { PagedResults } from "app/models/paged-results";

/** Constant representing the "data" parameter that will be looked at for a customizable Page Size to be returned */
export const RfidEncodingSchemesPagingResolver_PageSizeRouteData = "page_size";

/**
 * Background Resolver used to obtain the first page of the "RFID Encoding Schemes" Types for the 
 * "RFID Encoding Schemes" Infrastructure Page.
 * 
 * Allows for customizing the Page Size by using the "page_size" route data parameter.
 */
@Injectable()
export class RfidEncodingSchemesPagingResolver implements Resolve<PagedResults<ObjectDefinition>> {

  /**
   * Constructor
   */
  constructor(
    private objectService: ObjectService
  ) { }

  /**
   * Resolves the first page of the "RFID Encoding Scheme" Types for the Infrastructure 
   * "RFID Encoding Schemes" page.
   */
  async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<PagedResults<ObjectDefinition>> {
    // Get the Page Size to use, use the old default if nothing / invalid values are passed
    let pageSize = route.data[RfidEncodingSchemesPagingResolver_PageSizeRouteData];
    if (pageSize == null || pageSize == undefined || pageSize < 1) {
      console.warn(`Invalid Page Size found (${pageSize})!  Default Page Size (${PAGING_ITEMS_PER_PAGE}) will be used.`);
      pageSize = PAGING_ITEMS_PER_PAGE;
    }
    
    // Resolve / Return the "RFID Encoding Scheme" Types
    try {
      return await this.objectService.getAllObjectsForCategoryPaging(ObjectCategory.PASSIVE, 0, pageSize, 'ASC', "");
    } catch (error) {
      console.error("Problem resolving RFID Objects... Error: " + error, error);
      throw error;
    }
  }

}
